import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { emailSelector, userIdIreneSelector } from '../../../selectors';
import {
  getIsCurrentUserAnAdmin,
  getIsCurrentUserAnApplicationManager,
  getNeedsApplicationManager,
} from '../../../utils/businessVerification';
import { Anchor, Paragraph } from '@zillow/constellation';
import AdminContact from './AdminContact';
import styled from 'styled-components';

const StyledBold = styled.b`
  word-wrap: break-word;
`;

export default function MissingPermissionsContent({ data }) {
  const contactEmail = useSelector(emailSelector);
  const userId = useSelector(userIdIreneSelector);
  const userManagementCompanyId = data?.user?.userManagementCompany?.id;
  const isInUserManagementCompany = Boolean(userManagementCompanyId);
  const needsApplicationManager = getNeedsApplicationManager(data);
  const isCurrentUserAnAdmin = getIsCurrentUserAnAdmin(data, userId);
  const isCurrentUserAnApplicationManager = getIsCurrentUserAnApplicationManager(data, userId);
  if (!isInUserManagementCompany) {
    return (
      <Paragraph>
        You are currently signed in as <StyledBold>{contactEmail}</StyledBold>. Our records indicate that this email
        address does not have access to this application. Please log in with an email address associated with this
        application.
      </Paragraph>
    );
  }
  if (!needsApplicationManager && !isCurrentUserAnApplicationManager) {
    return (
      <>
        <Paragraph>
          Applications contain confidential information such as credit score, background report, and income documents.{' '}
          If you want access, ask your account’s application manager to assign you permission.
        </Paragraph>
      </>
    );
  }
  if (!needsApplicationManager && isCurrentUserAnApplicationManager) {
    // This only occurs if the user has successfully become application manager
    // It is displayed behind the application manager modal before it closes and they are navigated away
    return <Paragraph>Success! You are now the application manager.</Paragraph>;
  }
  if (needsApplicationManager && isCurrentUserAnAdmin) {
    return (
      <>
        <Paragraph marginBottom="sm">Your account doesn’t currently have an application manager assigned.</Paragraph>
        <Paragraph>
          Your account now allows up to 5 people to access applications. The application manager is the only person on
          the account who can assign those permissions.
        </Paragraph>
      </>
    );
  }
  return (
    <>
      <Paragraph>
        Applications contain confidential information such as credit score, background report, and income documents. In
        order for you to access applications, your account needs to set an application manager.{' '}
        <Anchor href="https://help.zillowrentalmanager.com/hc/en-us/articles/4417280719123">Find out more</Anchor>
      </Paragraph>
      <AdminContact marginTop="sm" data={data} />
    </>
  );
}

MissingPermissionsContent.propTypes = {
  data: PropTypes.object,
};
